import {
  ReactNode,
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { UserContext, useGroupMembership } from './UserContext';

// Constants
/**
 * Active Directory group name for data lead management permissions
 */
export const DATA_LEAD_MANAGEMENT_AD_GROUP = 'CHQ - DBO_DL_Assignments';

const UserDboFunctionalAreasQuery = gql`
  query UserDboFunctionalAreas($filter: FunctionalAreaListFilter) {
    devhub {
      connections {
        functionalAreas(filter: $filter) {
          id
          name
          dataBusinessOwner {
            login
          }
          dataLeads {
            login
          }
        }
      }
    }
  }
`;

interface FunctionalArea {
  id: number;
  name: string;
}

interface FunctionalAreaContextType {
  functionalAreas: FunctionalArea[];
  functionalAreaIds: number[];
  isDataBusinessOwner: boolean;
  canManageDataLeads: boolean;
  loading: boolean;
  refetch: () => void;
}

const FunctionalAreaContext = createContext<FunctionalAreaContextType>({
  functionalAreas: [],
  functionalAreaIds: [],
  isDataBusinessOwner: false,
  canManageDataLeads: false,
  loading: false,
  refetch: () => {},
});

interface FunctionalAreaProviderProps {
  children: ReactNode;
}

/**
 * Provider component for Functional Area context
 * @param props - Component props
 * @param props.children - Child components to render
 */
export const FunctionalAreaProvider: React.FC<FunctionalAreaProviderProps> = ({
  children,
}) => {
  const { login, isAdmin, isAdminLoading } = useContext(UserContext);
  const [functionalAreas, setFunctionalAreas] = useState<FunctionalArea[]>([]);
  const [functionalAreaIds, setFunctionalAreaIds] = useState<number[]>([]);
  const [isDataBusinessOwner, setIsDataBusinessOwner] = useState(false);
  const [canManageDataLeads, setCanManageDataLeads] = useState(false);

  // Check AD group membership for data lead management permissions
  const { loading: groupLoading, value: groupData } = useGroupMembership(
    login || '',
    [DATA_LEAD_MANAGEMENT_AD_GROUP],
  );

  // Memoized check for AD group membership
  const isInDataLeadManagementGroup = useMemo(() => {
    if (!groupData || groupLoading) return false;
    return (
      groupData.memberOf?.some(
        (group: any) => group.name === DATA_LEAD_MANAGEMENT_AD_GROUP,
      ) ?? false
    );
  }, [groupData, groupLoading]);

  const [fetchDboFunctionalAreas, { loading: functionalAreasLoading }] =
    useLazyQuery(UserDboFunctionalAreasQuery, {
      errorPolicy: 'all',
      variables: {
        filter: {
          dataBusinessOwner: login,
          dataLead: login,
        },
      },
      onCompleted: data => {
        if (data?.devhub?.connections?.functionalAreas) {
          const areas = data.devhub.connections.functionalAreas;
          setFunctionalAreas(areas);
          setFunctionalAreaIds(areas.map((area: FunctionalArea) => area.id));

          // Check if user is a data business owner for any area
          const hasDataBusinessOwnerAccess = areas.some((area: any) => {
            return (
              area.dataBusinessOwner?.login?.toLowerCase() ===
              login?.toLowerCase()
            );
          });
          setIsDataBusinessOwner(hasDataBusinessOwnerAccess);

          // Note: canManageDataLeads will be computed by the memoized hook
          // when functionalAreas state updates
        }
      },
      onError: _error => {
        // Error handling - could be logged to monitoring service in production
      },
    });

  useEffect(() => {
    if (login) {
      fetchDboFunctionalAreas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login]); // Remove fetchDboFunctionalAreas from deps to prevent infinite loop

  // Memoized permission calculation with short-circuit optimization
  const computedCanManageDataLeads = useMemo(() => {
    // Short-circuit: If user is a DevHub admin, they have access regardless of other roles
    if (isAdmin) {
      return true;
    }

    // Short-circuit: If user is in data lead management AD group, they have access regardless of roles
    if (isInDataLeadManagementGroup) {
      return true;
    }

    // Only check functional area roles if not in AD group and data is available
    if (functionalAreas.length === 0) {
      return false;
    }

    // Check role-based access (DBO or Data Lead)
    return functionalAreas.some((area: any) => {
      const isDbOwnerForArea =
        area.dataBusinessOwner?.login?.toLowerCase() === login?.toLowerCase();

      // Short-circuit within the area check too
      if (isDbOwnerForArea) {
        return true;
      }

      // Only check data leads if not DBO
      return area.dataLeads?.some(
        (lead: any) => lead.login?.toLowerCase() === login?.toLowerCase(),
      );
    });
  }, [functionalAreas, isInDataLeadManagementGroup, isAdmin, login]);

  // Update state when computed value changes
  useEffect(() => {
    setCanManageDataLeads(computedCanManageDataLeads);
  }, [computedCanManageDataLeads]);

  const refetch = () => {
    if (login) {
      fetchDboFunctionalAreas();
    }
  };

  // Combine loading states from functional areas, group membership, and admin check queries
  const loading = functionalAreasLoading || groupLoading || isAdminLoading;

  return (
    <FunctionalAreaContext.Provider
      value={{
        functionalAreas,
        functionalAreaIds,
        isDataBusinessOwner,
        canManageDataLeads,
        loading,
        refetch,
      }}
    >
      {children}
    </FunctionalAreaContext.Provider>
  );
};

/**
 * Hook to use Functional Area context
 * @returns Functional Area context value
 * @throws Error if used outside of FunctionalAreaProvider
 */
export const useFunctionalAreaContext = () => {
  const context = useContext(FunctionalAreaContext);
  if (!context) {
    throw new Error(
      'useFunctionalAreaContext must be used within a FunctionalAreaProvider',
    );
  }
  return context;
};
